import { isNumberValue, isStringValue } from '../validation/primitives';
/**
 * Deterministic rollout policy for the Q4 customer-experience work.
 *
 * The policy is deliberately pure so it can be used by a route, a server
 * component, or a browser measurement shim without a remote flag service. A
 * subject stays in the same cohort across requests; changing the percentage
 * only moves the boundary. Force-on/off lists are operational escape hatches
 * and should contain opaque database ids, never emails.
 */

export type CustomerExperienceCohort = 'control' | 'treatment';

export interface CustomerExperienceRolloutOptions {
  percent?: number;
  surface?: string;
  forceOn?: Iterable<string | number>;
  forceOff?: Iterable<string | number>;
}

export interface CustomerExperienceRolloutDecision {
  enabled: boolean;
  cohort: CustomerExperienceCohort;
  bucket: number;
  percent: number;
  reason: 'force_on' | 'force_off' | 'percentage';
  surface: string;
}

function normalizedPercent(value: unknown): number {
  const parsed = isNumberValue(value) ? value : Number(value);

  if (!Number.isFinite(parsed)) return 0;

  return Math.min(100, Math.max(0, parsed));
}

function envValue(name: string): string | undefined {
  if (typeof process === 'undefined') return undefined;
  // Next.js only exposes NEXT_PUBLIC_* values in the browser bundle. Keeping
  // the private name first lets server routes use the same code while an
  // explicitly mirrored public value keeps client-side RUM in the cohort.
  const value = process.env[name] ?? process.env[`NEXT_PUBLIC_${name}`];

  return isStringValue(value) && value.trim() ? value.trim() : undefined;
}

function parseIdList(value: string | undefined): Set<string> {
  return new Set(
    (value ?? '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean)
  );
}

/** Stable 0–99.99 bucket without pulling a crypto dependency into client code. */
export function rolloutBucket(subject: string | number, surface = 'customer-experience'): number {
  const input = `${surface}:${String(subject)}`;
  let hash = 2166136261;

  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return ((hash >>> 0) % 10_000) / 100;
}

export function customerExperienceRollout(
  subject: string | number,
  options: CustomerExperienceRolloutOptions = {}
): CustomerExperienceRolloutDecision {
  const surface = options.surface?.trim() || 'customer-experience';
  const subjectKey = String(subject);

  const forceOn = new Set(
    Array.from(options.forceOn ?? parseIdList(envValue('CUSTOMER_EXPERIENCE_FORCE_ON'))).map(String)
  );

  const forceOff = new Set(
    Array.from(options.forceOff ?? parseIdList(envValue('CUSTOMER_EXPERIENCE_FORCE_OFF'))).map(
      String
    )
  );

  const defaultPercent =
    typeof process !== 'undefined' && process.env.NODE_ENV === 'development' ? 100 : 0;

  const percent = normalizedPercent(
    options.percent ?? envValue('CUSTOMER_EXPERIENCE_ROLLOUT_PERCENT') ?? defaultPercent
  );

  const bucket = rolloutBucket(subjectKey, surface);

  if (forceOff.has(subjectKey)) {
    return { enabled: false, cohort: 'control', bucket, percent, reason: 'force_off', surface };
  }

  if (forceOn.has(subjectKey)) {
    return { enabled: true, cohort: 'treatment', bucket, percent, reason: 'force_on', surface };
  }

  const enabled = bucket < percent;

  return {
    enabled,
    cohort: enabled ? 'treatment' : 'control',
    bucket,
    percent,
    reason: 'percentage',
    surface,
  };
}
